// Background study reminders: runs on every page while a student is signed in.
// Shows an in-app card (and a system notification when allowed) shortly before a
// timetable session starts, and a "time's up — did you finish?" check-in at the end.
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BellRing, BookOpen, Check, Clock, X } from "lucide-react";
import api from "../../services/api";
import { pushEnabledOnThisDevice } from "../../services/push";
import "./StudyReminders.css";

export const TIMETABLE_UPDATED = "eg-timetable-updated";
export const TEST_REMINDER = "eg-test-reminder";

const TICK_MS = 20 * 1000;
const REFRESH_MS = 5 * 60 * 1000;
const START_WINDOW = 3; // minutes after the reminder time we still show it
const END_WINDOW = 10; // minutes after a session ends we still ask
const PUBLIC_PATHS = ["/", "/login", "/register", "/about", "/contact", "/verify", "/forgot-password", "/reset-password"];

const hasToken = () =>
  Boolean(localStorage.getItem("access_token") || sessionStorage.getItem("access_token"));

const toMinutes = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

const formatTime = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, "0")}${h >= 12 ? "pm" : "am"}`;
};

// Current date/time in Nigeria, independent of the device's timezone
const lagosNow = () => {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Africa/Lagos",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    })
      .formatToParts(new Date())
      .map((p) => [p.type, p.value])
  );
  return { date: `${parts.year}-${parts.month}-${parts.day}`, minutes: Number(parts.hour) * 60 + Number(parts.minute) };
};

// Remember which reminders were already shown (per day) so they don't repeat
const FIRED_KEY = "eg-reminders-fired";
const loadFired = () => {
  try {
    return JSON.parse(localStorage.getItem(FIRED_KEY) || "{}");
  } catch {
    return {};
  }
};
const saveFired = (fired) => {
  try {
    localStorage.setItem(FIRED_KEY, JSON.stringify(fired));
  } catch {
    /* storage unavailable: reminders may repeat after reload, which is harmless */
  }
};

const systemNotify = (title, body, onClick, tag) => {
  try {
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    // With web push on, the service worker delivers system notifications instead
    if (pushEnabledOnThisDevice()) return;
    // Only use a system notification when the app isn't in front; otherwise the in-app card is enough
    if (!document.hidden && document.hasFocus()) return;
    const n = new Notification(title, { body, icon: "/logo.png", tag: tag || title, renotify: true });
    n.onclick = () => {
      window.focus();
      onClick?.();
      n.close();
    };
  } catch {
    /* some browsers (e.g. iOS Safari) don't support page notifications */
  }
};

const StudyReminders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [timetable, setTimetable] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const firedRef = useRef(loadFired());

  const active = hasToken() && !PUBLIC_PATHS.includes(location.pathname);

  const refresh = useCallback(async () => {
    if (!hasToken()) return;
    try {
      const res = await api.get("/api/timetable");
      setTimetable(res.data);
    } catch {
      /* not fatal: try again on the next refresh */
    }
  }, []);

  const push = useCallback((alert) => {
    setAlerts((list) => [alert, ...list.filter((a) => a.id !== alert.id)].slice(0, 3));
  }, []);

  const dismiss = (id) => setAlerts((list) => list.filter((a) => a.id !== id));

  const studyNow = useCallback(
    (block) => navigate("/agents/tutor", { state: { context: { subject: block.subject, topic: block.topic } } }),
    [navigate]
  );

  const markDone = async (alert) => {
    try {
      const res = await api.post(`/api/timetable/blocks/${alert.block.id}/done`, { date: alert.date, done: true });
      setTimetable(res.data);
      window.dispatchEvent(new Event(TIMETABLE_UPDATED));
      setAlerts((list) => list.map((a) => (a.id === alert.id ? { ...a, kind: "done" } : a)));
      setTimeout(() => dismiss(alert.id), 2500);
    } catch {
      setAlerts((list) => list.map((a) => (a.id === alert.id ? { ...a, error: "Couldn't save — try from your Timetable" } : a)));
    }
  };

  // Load the timetable when signed in, refresh periodically and when it changes elsewhere
  useEffect(() => {
    if (!active) return undefined;
    refresh();
    const interval = setInterval(refresh, REFRESH_MS);
    const onUpdate = () => refresh();
    const onVisible = () => !document.hidden && refresh();
    window.addEventListener(TIMETABLE_UPDATED, onUpdate);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener(TIMETABLE_UPDATED, onUpdate);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [active, refresh]);

  // "Send a test reminder" from the Timetable settings
  useEffect(() => {
    const onTest = () => {
      const block = { id: "test", subject: "Mathematics", topic: "Fractions", start: "17:00", end: "17:45" };
      push({ id: `test-${Date.now()}`, kind: "start", block, lead: 5, test: true });
      systemNotify("Mathematics starts in 5 minutes", "This is a test reminder from MyEduGenius.");
    };
    window.addEventListener(TEST_REMINDER, onTest);
    return () => window.removeEventListener(TEST_REMINDER, onTest);
  }, [push]);

  // The clock: check every 20 seconds whether a reminder is due
  useEffect(() => {
    if (!active || !timetable) return undefined;

    const check = () => {
      const { reminders, blocks = [], completions = {} } = timetable;
      if (!reminders?.enabled) return;
      const now = lagosNow();
      const todayIndex = (new Date(`${now.date}T12:00:00`).getDay() + 6) % 7; // Monday = 0
      const fired = firedRef.current;
      let changed = false;

      // Forget reminders from previous days
      Object.keys(fired).forEach((key) => {
        if (!key.startsWith(now.date)) {
          delete fired[key];
          changed = true;
        }
      });

      blocks
        .filter((b) => b.day === todayIndex)
        .forEach((block) => {
          const start = toMinutes(block.start);
          const end = toMinutes(block.end);
          const lead = reminders.minutes_before || 0;
          const remindAt = start - lead;
          const done = (completions[now.date] || []).includes(block.id);

          const startKey = `${now.date}:${block.id}:start`;
          if (!fired[startKey] && !done && now.minutes >= remindAt && now.minutes < remindAt + START_WINDOW && now.minutes < end) {
            fired[startKey] = true;
            changed = true;
            const minsLeft = start - now.minutes;
            push({ id: startKey, kind: "start", block, lead: minsLeft, date: now.date });
            systemNotify(
              minsLeft > 0 ? `${block.subject} starts in ${minsLeft} minute${minsLeft === 1 ? "" : "s"}` : `Time to study ${block.subject}`,
              `${formatTime(block.start)} – ${formatTime(block.end)}${block.topic ? ` · ${block.topic}` : ""}`,
              () => studyNow(block),
              startKey
            );
          }

          const endKey = `${now.date}:${block.id}:end`;
          if (reminders.end_prompt && !fired[endKey] && !done && now.minutes >= end && now.minutes < end + END_WINDOW) {
            fired[endKey] = true;
            changed = true;
            push({ id: endKey, kind: "end", block, date: now.date });
            systemNotify(
              `Time's up for ${block.subject}`,
              "Did you finish? Tick it off in your timetable.",
              () => navigate("/dashboard"),
              endKey
            );
          }
        });

      if (changed) saveFired(fired);
    };

    check();
    const timer = setInterval(check, TICK_MS);
    return () => clearInterval(timer);
  }, [active, timetable, push, studyNow, navigate]);

  if (!active || alerts.length === 0) return null;

  return (
    <div className="sr-stack" role="region" aria-label="Study reminders">
      {alerts.map((alert) => {
        const { block } = alert;
        const time = `${formatTime(block.start)} – ${formatTime(block.end)}`;
        return (
          <div key={alert.id} className={`sr-card sr-${alert.kind}`} role="alert">
            <span className="sr-icon" aria-hidden="true">
              {alert.kind === "end" ? <Clock size={18} /> : alert.kind === "done" ? <Check size={18} /> : <BellRing size={18} />}
            </span>
            <div className="sr-body">
              {alert.kind === "start" && (
                <>
                  <strong>
                    {alert.lead > 0
                      ? `${block.subject} starts in ${alert.lead} minute${alert.lead === 1 ? "" : "s"}`
                      : `Time to study ${block.subject}`}
                  </strong>
                  <span>
                    {time}
                    {block.topic ? ` · ${block.topic}` : ""}
                    {alert.test ? " · test reminder" : ""}
                  </span>
                  <div className="sr-actions">
                    <button type="button" className="sr-primary" onClick={() => { dismiss(alert.id); studyNow(block); }}>
                      <BookOpen size={14} /> Study now
                    </button>
                    <button type="button" className="sr-secondary" onClick={() => dismiss(alert.id)}>
                      Dismiss
                    </button>
                  </div>
                </>
              )}
              {alert.kind === "end" && (
                <>
                  <strong>Time's up for {block.subject}!</strong>
                  <span>Did you finish your session? ({time})</span>
                  {alert.error && <span className="sr-error">{alert.error}</span>}
                  <div className="sr-actions">
                    <button type="button" className="sr-primary" onClick={() => markDone(alert)}>
                      <Check size={14} /> Mark done
                    </button>
                    <button type="button" className="sr-secondary" onClick={() => dismiss(alert.id)}>
                      Not yet
                    </button>
                  </div>
                </>
              )}
              {alert.kind === "done" && (
                <>
                  <strong>Great work! 🎉</strong>
                  <span>{block.subject} is ticked off for today.</span>
                </>
              )}
            </div>
            <button type="button" className="sr-close" onClick={() => dismiss(alert.id)} aria-label="Close reminder">
              <X size={16} />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default StudyReminders;
